import { GetGeoDatasetExternalLayersPayload } from 'data/geodatasets';
import {
  ApplicationId,
  GeoDatasetValueOptionDynamicValues,
  GetGeoDatasetValueOptionsDynamicValuesPayload,
} from 'interfaces';

import {
  BlobStorageItem,
  CreateGeoDatasetPayload,
  GeoDataset,
  GeoDatasetColour,
  GeoDatasetDimensionFilter,
  GeoDatasetExternalLayer,
  GeoDatasetExternalLayerJsonData,
  GeoDatasetId,
  GeoDatasetInternalLayer,
  GeoDatasetInternalLayersGeoCoordinates,
  GeoDatasetInternalLayersGeoCoordinatesPayload,
  GeoDatasetPropertiesResult,
  LayerGeoJsonData,
  UpdateGeoDatasetPayload,
  UpdateGeoDatasetsPartialPayload,
  UploadGeoDatasetExternalLayerFilePayload,
} from 'interfaces/geodataset';

import { api, toFormData } from './api';

/**
 * Fetches a single geo dataset belonging to the specified application.
 * @param applicationId The application ID.
 * @param geoDataSetId The geo dataset ID.
 */
export function getGeoDataset(applicationId: ApplicationId, geoDataSetId: GeoDatasetId) {
  return api.get<GeoDataset>(`/applications/${applicationId}/geodatasets/${geoDataSetId}`, {
    params: {
      applicationId,
      geoDataSetId,
    },
  });
}

/**
 * Fetches all external-layers of a geo dataset map belonging to the specified application.
 * @param applicationId The application ID.
 * @param geoDataSetId The geo dataset ID.
 * @param payload The request payload used to fetch all external layers.
 */
export function getGeoDatasetExternalLayers(
  applicationId: ApplicationId,
  geoDataSetId: GeoDatasetId,
  payload: GetGeoDatasetExternalLayersPayload
) {
  return api.post<GeoDatasetExternalLayer[]>(
    `/applications/${applicationId}/geodatasets/${geoDataSetId}/external-layers`,
    payload
  );
}

/**
 * Fetches all dimension filters of a geo dataset map belonging to the specified application.
 * @param applicationId The application ID.
 * @param geoDataSetId The geo dataset ID.
 */
export function getGeoDatasetDimensionFiltersLayers(
  applicationId: ApplicationId,
  geoDataSetId: GeoDatasetId
) {
  return api.get<GeoDatasetDimensionFilter[]>(
    `/applications/${applicationId}/geodatasets/${geoDataSetId}/dimension-filters`
  );
}

/**
 * Creates a new geo dataset.
 * @param applicationId The application ID.
 * @param payload The request payload used to create a geo dataset.
 * @return A `Promise` of the newly created geo dataset payload.
 */
export function getGeoDatasetInternalLayers(
  applicationId: ApplicationId,
  geoDataSetId: GeoDatasetId
) {
  return api.get<GeoDatasetInternalLayer[]>(
    `/applications/${applicationId}/geodatasets/${geoDataSetId}/internal-layers`
  );
}

/**
 * Creates a new geo dataset.
 * @param applicationId The application ID.
 * @param payload The request payload used to create a geo dataset.
 * @return A `Promise` of the newly created geo dataset payload.
 */
export function getGeoDatasetInternalLayersGeoCoordinates(
  applicationId: ApplicationId,
  geoDataSetId: GeoDatasetId,
  payload: GeoDatasetInternalLayersGeoCoordinatesPayload
) {
  return api.post<GeoDatasetInternalLayersGeoCoordinates[]>(
    `/applications/${applicationId}/geodatasets/${geoDataSetId}/internal-layers-geo-coordinates`,
    payload
  );
}

/**
 * Fetches all geo datasets belonging to the specified application.
 * @param applicationId the application ID.
 */
export function getGeoDatasets(applicationId: ApplicationId) {
  return api.get<GeoDataset[]>(`/applications/${applicationId}/geodatasets`, {
    params: {
      applicationId,
    },
  });
}

/**
 * Fetches all geo dataset colours belonging to the specified application.
 * @param applicationId the application ID.
 */
export function getGeoDatasetColours(applicationId: ApplicationId) {
  return api.get<GeoDatasetColour[]>(`/applications/${applicationId}/geodatasets/colours`);
}

/**
 * Creates a new geo dataset.
 * @param applicationId The application ID.
 * @param payload The request payload used to create a geo dataset.
 * @return A `Promise` of the newly created geo dataset payload.
 */
export function createGeoDataset(applicationId: ApplicationId, payload: CreateGeoDatasetPayload) {
  return api.post<GeoDataset>(`/applications/${applicationId}/geodatasets`, payload);
}

/**
 * Updates a geo dataset.
 * @param applicationId The application ID.
 * @param geoDatasetId The geo dataset ID.
 * @param payload The request payload used to update the geo dataset.
 * @return A `Promise` of the updated payload.
 */
export function updateGeoDataset(
  applicationId: ApplicationId,
  geoDatasetId: GeoDatasetId,
  payload: UpdateGeoDatasetPayload
): Promise<GeoDataset> {
  return api.put<GeoDataset>(`/applications/${applicationId}/geodatasets/${geoDatasetId}`, payload);
}

/**
 * Deletes an existing geo dataset belonging to the specified application.
 * @param applicationId The application ID.
 * @param geoDatasetId The geo dataset ID.
 */
export function deleteGeoDataset(applicationId: ApplicationId, geoDatasetId: GeoDatasetId) {
  return api.delete(`/applications/${applicationId}/geodatasets/${geoDatasetId}`);
}

/**
 * Calculates the centroid data for a geo dataset belonging to the specified application.
 * @param applicationId The application ID.
 * @param geoDatasetId The geo dataset ID.
 */
export function calculateGeoDatasetCentroidData(
  applicationId: ApplicationId,
  geoDatasetId: GeoDatasetId
) {
  return api.get<GeoDatasetPropertiesResult>(
    `/applications/${applicationId}/geodatasets/${geoDatasetId}/centroid`
  );
}

/**
 * Gets the json data for a geo dataset layer belonging to the specified application.
 * @param applicationId The application ID.
 * @param isFileBlob The type of the file data being used.
 * @param layerUriOrBlobName The geo dataset layer uri or blob name.
 */
export async function getLayerGeoJsonData(
  applicationId: ApplicationId,
  isFileBlob: boolean,
  layerUriOrBlobName: string
) {
  return api.get<LayerGeoJsonData>(`/applications/${applicationId}/geodatasets/layer-json-data`, {
    params: {
      isFileBlob,
      layerUriOrBlobName,
    },
  });
}

/**
 * Gets the geo dataset blob container content belonging to the specified application.
 * @param applicationId The application ID.
 */
export function getGeoDatasetBlobContainerContent(applicationId: ApplicationId) {
  return api.get<BlobStorageItem[]>(
    `/applications/${applicationId}/geodatasets/blob-container-content`
  );
}

/**
 * Gets the geo dataset blob container content belonging to the specified application.
 * @param applicationId The application ID.
 */
export function getGeoDatasetIconsBlobContainerContent(applicationId: ApplicationId) {
  return api.get<BlobStorageItem[]>(
    `/applications/${applicationId}/geodatasets/blob-container-content/icons`
  );
}

/**
 * Gets the geo dataset external layers json data belonging to the specified application.
 * @param applicationId The application ID.
 * @param geoDatasetId The Geodataset ID.
 */
export async function getGeoDatasetExternalLayersJsonData(
  applicationId: ApplicationId,
  geoDatasetId: GeoDatasetId
) {
  return await api.get<GeoDatasetExternalLayerJsonData[]>(
    `/applications/${applicationId}/geodatasets/${geoDatasetId}/layers-json-data`
  );
}

/**
 * Uploads a geo dataset external layer file to azure blob storage.
 * @param applicationId The application ID.
 * @param payload The request payload used to upload a file.
 */
export function uploadGeoDatasetExternalLayerFile(
  applicationId: ApplicationId,
  payload: UploadGeoDatasetExternalLayerFilePayload
) {
  return api.post<BlobStorageItem[]>(
    `/applications/${applicationId}/geodatasets/external-layer-file`,
    toFormData(payload)
  );
}

/**
 * Updates geodatasets partially within the specified application
 * @param applicationId The application ID.
 * @param payload The partial updates for geodatasets.
 */
export function updateGeoDatasetsPartial(
  applicationId: ApplicationId,
  payload: UpdateGeoDatasetsPartialPayload
) {
  return api.patch(`/applications/${applicationId}/geodatasets`, payload);
}

/**
 * Fetches the value options for a dynamic value option.
 * @param applicationId The application ID.
 * @param geoDatasetId The geo dataset ID.
 */
export function getGeoDatasetValueOptionsDynamicValues(
  applicationId: ApplicationId,
  geoDatasetId: GeoDatasetId,
  payload: GetGeoDatasetValueOptionsDynamicValuesPayload
) {
  return api.post<GeoDatasetValueOptionDynamicValues[]>(
    `/applications/${applicationId}/geodatasets/${geoDatasetId}/value-options-dynamic-values`,
    payload
  );
}
